import React, { useState } from "react";

import { Box, Button, Typography } from "@mui/material";
import type { CardFilters } from "store/types";

import { useFilterOptions } from "../../hooks/useFilterOptions";
import { CardFilterSelect } from "../CardFilterSelect";
import { SectionLabel } from "./SectionLabel";

interface Props {
  cardFilters?: CardFilters;
  onUpdate: (f: CardFilters | undefined) => void;
}

export const CardFilterSettings: React.FC<Props> = ({ cardFilters, onUpdate }) => {
  const { investorOptions, assetClassOptions } = useFilterOptions();
  const hasFilters = cardFilters && (cardFilters.investors.length > 0 || cardFilters.assetClasses.length > 0);
  const [expanded, setExpanded] = useState(!!hasFilters);

  const filters: CardFilters = cardFilters ?? { investors: [], assetClasses: [] };

  const handleInvestorsChange = (investors: string[]) => {
    const ac = filters.assetClasses;
    onUpdate(investors.length === 0 && ac.length === 0 ? undefined : { investors, assetClasses: ac });
  };

  const handleAssetClassesChange = (assetClasses: string[]) => {
    const inv = filters.investors;
    onUpdate(inv.length === 0 && assetClasses.length === 0 ? undefined : { investors: inv, assetClasses });
  };

  return (
    <Box>
      <Box
        onClick={() => setExpanded((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          mb: expanded ? 1 : 0
        }}
      >
        <SectionLabel>
          Card Filters{" "}
          {hasFilters && (
            <Box component="span" sx={{ color: "#1A3C34", fontSize: 10 }}>
              ({filters.investors.length + filters.assetClasses.length})
            </Box>
          )}
        </SectionLabel>
        <Typography sx={{ fontSize: 10, color: "#9CA3AF" }}>{expanded ? "▲" : "▼"}</Typography>
      </Box>

      {!expanded && !hasFilters && (
        <Typography sx={{ fontSize: 11, color: "#D1D5DB" }}>Using global filters</Typography>
      )}

      {expanded && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <CardFilterSelect
            label="Investor"
            options={investorOptions}
            selected={filters.investors}
            onChange={handleInvestorsChange}
            tone="blue"
          />
          <CardFilterSelect
            label="Asset Class"
            options={assetClassOptions}
            selected={filters.assetClasses}
            onChange={handleAssetClassesChange}
            tone="blue"
          />
          {hasFilters && (
            <Button
              onClick={() => onUpdate(undefined)}
              sx={{
                mt: 1,
                width: "100%",
                p: "5px 0",
                border: "1px solid #E5E7EB",
                borderRadius: "4px",
                fontSize: 11,
                color: "#6B7280",
                textTransform: "none"
              }}
            >
              Clear card filters
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
